import { BuildStatus } from '@cadstrom/cadstrom-shared';

import { canCustomerTriggerAnalysis } from '#src/routes/results/analysis-results-page/components/analyze-trigger-visibility.js';

import type { RevisionStatus, RevisionWithFiles } from '@cadstrom/cadstrom-shared';

/** The fields a revision's position on the rail is read from. */
export interface PositionedRevision extends Pick<RevisionWithFiles, 'status' | 'hasUploadedFiles' | 'automationMode'> {
	/** Whether the revision's organization is on the billing flow that owns the Analyze gate. */
	showBillingFlow: boolean;
}

/**
 * What a revision's current position means for the person looking at it.
 *
 * - `action`: the pipeline has stopped and is waiting on a person.
 * - `progress`: queued or running; nothing is expected of the engineer.
 * - `done`: results are ready.
 * - `failed`: the revision errored.
 */
export type FlowTone = 'action' | 'progress' | 'done' | 'failed';

/** Background and text colour for a tone. */
export interface FlowToneClasses {
	/** Background class, for a rail segment or a status dot. */
	fill: string;
	text: string;
}

/**
 * Tailwind classes per tone, on the `11` step of each scale rather than the `9`
 * the status dots use: `iris-9` and `red-9` fall below the 3:1 contrast floor
 * against the card, and `11` also matches the suffix label beside the segment.
 */
export const FLOW_TONE_CLASSES: Record<FlowTone, FlowToneClasses> = {
	action: { fill: 'bg-amber-11', text: 'text-amber-11' },
	progress: { fill: 'bg-blue-11', text: 'text-blue-11' },
	done: { fill: 'bg-green-11', text: 'text-green-11' },
	failed: { fill: 'bg-red-11', text: 'text-red-11' },
};

/** One step of the rail. */
export interface FlowStep {
	name: string;
	/** Statuses this step covers, in pipeline order. */
	statuses: readonly RevisionStatus[];
	/** Copy for a step the revision has not reached. */
	ahead: string;
	/** Copy for a step the revision has cleared. */
	done?: string;
	/** Copy when the revision carries no position at all. Defaults to `ahead`. */
	inert?: string;
}

/**
 * The five steps of the pipeline, in order.
 *
 * `Results` carries no `done` copy because a revision can never be beyond the
 * last step. `Files` keeps `ahead` only as its inert copy, since a revision can
 * never be short of the first step either.
 */
export const FLOW_STEPS = [
	{
		name: 'Files',
		statuses: [BuildStatus.Pending],
		ahead: 'The schematic, layout, and BOM exports for this revision.',
		done: 'Files are uploaded and stored with this revision. Changing them takes a new revision.',
	},
	{
		name: 'Ingest',
		statuses: [BuildStatus.Validating, BuildStatus.Processing],
		ahead:
			'Files are checked for type, structure, and missing references, then the design is read: components, nets, and the bill of materials all come out of the schematic itself.',
		done: 'Design files have been ingested and parsed. Components, nets, and the bill of materials are extracted and stored.',
	},
	{
		name: 'BOM review',
		statuses: [BuildStatus.BomAwaitingReview, BuildStatus.ProcessingBom],
		ahead:
			'Every BOM line is matched to a real part. You then confirm the matches and correct anything that could not be resolved.',
		done: 'The bill of materials is confirmed and your corrections are merged in. Analysis runs against the parts you approved.',
	},
	{
		name: 'Analysis',
		statuses: [
			BuildStatus.ReadyToAnalyze,
			BuildStatus.CreditRequired,
			BuildStatus.ProcessingDesign,
			BuildStatus.AnalysisComplete,
		],
		ahead:
			'Deterministic and AI checks across power architecture, pinouts, bus configuration, and datasheet conformance.',
		done: 'The checks have finished. Anything they turned up is on the results page.',
	},
	{
		name: 'Results',
		statuses: [BuildStatus.Complete],
		ahead:
			'Once the checks finish, every finding arrives with the evidence behind it: the datasheet page, the net, the pin.',
	},
] as const satisfies readonly FlowStep[];

/** Where a revision sits on the rail, and how that position reads. */
export interface FlowPosition {
	/** Index into {@link FLOW_STEPS}, or null when the status carries no position. */
	activeIndex: number | null;
	tone: FlowTone;
	/** Short status phrase appended to the active step's label. */
	suffix: string;
	/** Phrase shown in the tooltip beside the step name. */
	badge: string;
	/** Tooltip copy for the active step. */
	body: string;
	/** Whether work is executing right now, which is what the sheen marks. */
	isRunning: boolean;
}

const PENDING_AWAITING_FILES: FlowPosition = {
	activeIndex: 0,
	tone: 'action',
	suffix: 'upload files',
	badge: 'Action needed',
	body: 'Waiting for you to upload files. Nothing else runs until the first one lands.',
	isRunning: false,
};

const PENDING_QUEUED: FlowPosition = {
	activeIndex: 0,
	tone: 'progress',
	suffix: 'queued',
	badge: 'In progress',
	body: 'Your files have been uploaded and will be automatically validated.',
	isRunning: false,
};

const ANALYSIS_QUEUED: FlowPosition = {
	activeIndex: 3,
	tone: 'progress',
	suffix: 'queued',
	badge: 'In progress',
	body: 'The bill of materials is settled and the checks will start on their own. Nothing is needed from you.',
	isRunning: false,
};

const POSITIONS: Partial<Record<RevisionStatus, FlowPosition>> = {
	[BuildStatus.Validating]: {
		activeIndex: 1,
		tone: 'progress',
		suffix: 'validating',
		badge: 'In progress',
		body: 'Validating file types, project type, and any references the design depends on.',
		isRunning: true,
	},
	[BuildStatus.Processing]: {
		activeIndex: 1,
		tone: 'progress',
		suffix: 'reading design',
		badge: 'In progress',
		body: 'Reading the design. Components, nets, and the bill of materials are being extracted from the schematic.',
		isRunning: true,
	},
	[BuildStatus.BomAwaitingReview]: {
		activeIndex: 2,
		tone: 'action',
		suffix: 'action needed',
		badge: 'Action needed',
		body: 'Every BOM line that could be matched to a real part has been. Confirm those and correct the rest, so analysis runs against the parts you approve.',
		isRunning: false,
	},
	[BuildStatus.ProcessingBom]: {
		activeIndex: 2,
		tone: 'progress',
		suffix: 'reviewing',
		badge: 'In progress',
		body: 'Your corrections are being merged back into the bill of materials. No action needed.',
		isRunning: true,
	},
	[BuildStatus.ReadyToAnalyze]: {
		activeIndex: 3,
		tone: 'action',
		suffix: 'ready',
		badge: 'Action needed',
		body: 'The bill of materials is settled, so analysis is what happens next: deterministic and AI checks across power architecture, pinouts, bus configuration, and datasheet conformance.',
		isRunning: false,
	},
	[BuildStatus.CreditRequired]: {
		activeIndex: 3,
		tone: 'action',
		suffix: 'credit required',
		badge: 'Action needed',
		body: 'Your account needs updating. You have exceeded the available number of active design review projects.',
		isRunning: false,
	},
	[BuildStatus.ProcessingDesign]: {
		activeIndex: 3,
		tone: 'progress',
		suffix: 'in progress',
		badge: 'In progress',
		body: "Deterministic and AI checks are running across every part, pin, and net: power architecture, pinouts, bus configuration, and datasheet conformance. You'll be notified when they finish.",
		isRunning: true,
	},
	[BuildStatus.AnalysisComplete]: {
		activeIndex: 3,
		tone: 'progress',
		suffix: 'preparing results',
		badge: 'In progress',
		body: "The checks have finished. Your results are being prepared for release, and you'll be notified when they're ready.",
		isRunning: false,
	},
	[BuildStatus.Complete]: {
		activeIndex: 4,
		tone: 'done',
		suffix: 'ready',
		badge: 'Finished',
		body: 'Every finding is here with the evidence behind it. Open one to see the exact datasheet page, net, or pin it was checked against.',
		isRunning: false,
	},
};

const NO_POSITION: FlowPosition = {
	activeIndex: null,
	tone: 'failed',
	suffix: '',
	badge: '',
	body: '',
	isRunning: false,
};

/**
 * Resolve where a revision sits on the rail.
 *
 * Two statuses resolve two ways. `pending` waits on the customer with no files
 * uploaded and is queued with them. The analysis gate waits on a person only
 * where the customer is the one who can clear it, by the same rule the results
 * page offers its Analyze button by: under automation that dispatches the run,
 * or for an organization whose runs Cadstrom dispatches, it reads as queued
 * work instead.
 *
 * @param revision Revision to place.
 * @returns The position, whose `activeIndex` is null for `error`, `abandoned`,
 *   and `unknown`, which describe a stopped revision rather than a point in the
 *   flow.
 */
export function getFlowPosition(revision: PositionedRevision): FlowPosition {
	if (revision.status === BuildStatus.Pending) {
		return revision.hasUploadedFiles ? PENDING_QUEUED : PENDING_AWAITING_FILES;
	}
	if (
		revision.status === BuildStatus.ReadyToAnalyze &&
		!canCustomerTriggerAnalysis({
			revisionStatus: revision.status,
			revisionAutomationMode: revision.automationMode,
			showBillingFlow: revision.showBillingFlow,
			isAnalyzing: false,
		})
	) {
		return ANALYSIS_QUEUED;
	}

	return POSITIONS[revision.status] ?? NO_POSITION;
}

/**
 * The step a revision currently sits on.
 *
 * @param position Where the revision currently sits.
 * @returns The step, or null when the status carries no position.
 */
export function getFlowStep(position: FlowPosition): FlowStep | null {
	return position.activeIndex === null ? null : (FLOW_STEPS[position.activeIndex] ?? null);
}

/**
 * Whether the revision has cleared the whole pipeline.
 *
 * @param position Where the revision currently sits.
 * @returns True once results are ready, when no step is current and the rail
 *   reads as finished end to end rather than marking a position.
 */
export function isFlowFinished(position: FlowPosition): boolean {
	return position.tone === 'done';
}

/**
 * Whether the rail can express a status at all.
 *
 * @param status Status to test.
 * @returns False for the statuses that carry no position in the flow.
 */
export function isFlowPositioned(status: RevisionStatus): boolean {
	return status === BuildStatus.Pending || POSITIONS[status] !== undefined;
}

/** How one step of the rail relates to the revision's current position. */
export type FlowStepPhase = 'done' | 'active' | 'ahead' | 'inert';

/**
 * Classify a step against a position.
 *
 * @param index Index of the step in {@link FLOW_STEPS}.
 * @param position Where the revision currently sits.
 * @returns `inert` when the revision carries no position, otherwise the step's
 *   relation to the active one.
 */
export function getFlowStepPhase(index: number, position: FlowPosition): FlowStepPhase {
	if (position.activeIndex === null) {
		return 'inert';
	}
	if (index < position.activeIndex) {
		return 'done';
	}
	return index === position.activeIndex ? 'active' : 'ahead';
}

/**
 * Pick the tooltip copy for a step, which changes tense with the phase.
 *
 * @param step Step being described.
 * @param phase The step's relation to the revision's position.
 * @param position Where the revision currently sits.
 * @returns The copy to show.
 */
export function getFlowStepBody(step: FlowStep, phase: FlowStepPhase, position: FlowPosition): string {
	switch (phase) {
		case 'done': {
			return step.done ?? step.ahead;
		}
		case 'active': {
			return position.body;
		}
		case 'inert': {
			return step.inert ?? step.ahead;
		}
		case 'ahead': {
			return step.ahead;
		}
	}
}
